import {
  type ChangeEvent,
  type CompositionEvent,
  useRef,
  useState
} from 'react';

/**
 * The empty line's input of a list: what is typed there starts a row once the
 * kind can read it ("-" waits for "-5"), never in the middle of an IME
 * composition. Until then the text stays in the empty line. Keys that land
 * here before the new row's input takes the focus go on into that row
 * (`extend`), not into a row of their own.
 */
export function usePlaceholderTyping<V>(
  read: (text: string) => V | null,
  start: (value: V) => void,
  /** Gives the row just started its whole text so far. */
  extend: (value: V) => void
) {
  const [text, setText] = useState('');
  // The text of the row just started, while the focus is still here.
  const started = useRef<string | null>(null);

  function take(next: string) {
    if (started.current !== null) {
      const whole = started.current + next;
      const value = read(whole);
      if (value !== null) {
        started.current = whole;
        setText('');
        extend(value);
        return;
      }
    }
    const value = next.trim() === '' ? null : read(next);
    if (value === null) {
      setText(next);
      return;
    }
    started.current = next;
    setText('');
    start(value);
  }

  return {
    value: text,
    onChange(event: ChangeEvent<HTMLInputElement>) {
      if ((event.nativeEvent as InputEvent).isComposing) {
        setText(event.target.value);
        return;
      }
      take(event.target.value);
    },
    onCompositionEnd(event: CompositionEvent<HTMLInputElement>) {
      take(event.currentTarget.value);
    },
    onBlur() {
      started.current = null;
    }
  };
}
